import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  TableSortLabel,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { getWheelSourceLabel } from '../../shared/utils/wheelSource';

interface Wheel {
  _id: string;
  name: string;
  type: string;
  pcd: string;
  et: string;
  dia: string;
  price: number;
  diameter: string;
  isMy?: boolean;
  width: string;
  bestseller?: boolean;
  source?: string;
}

type SortBy = '' | 'diameter' | 'pcd';
type SortDir = 'asc' | 'desc';

const WheelRegistryPage = () => {
  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [prioritizeMy, setPrioritizeMy] = useState(false);
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [sortBy, setSortBy] = useState<SortBy>('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [bestseller, setBestseller] = useState(false);

  const handleSort = (column: Exclude<SortBy, ''>) => {
    setPage(1);
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  useEffect(() => {
    const fetchWheels = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/admin/wheels', {
          params: {
            page,
            limit,
            search: searchQuery,
            prioritizeMy,
            unassignedOnly,
            sortBy: sortBy || undefined,
            sortDir,
            bestseller,
          },
        });
        setWheels(response.data.data);
        setTotalPages(response.data.pages);
      } catch (err) {
        setError('Ошибка при загрузке дисков.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchWheels();
  }, [page, searchQuery, prioritizeMy, unassignedOnly, sortBy, sortDir, bestseller]);

  const handleAddClick = () => {
    navigate('/wheels/create');
  };

  const handleDelete = async (wheelId: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот диск?')) return;

    try {
      await api.delete(`/api/admin/wheels/${wheelId}`);
      setWheels((prev) => prev.filter((w) => w._id !== wheelId));
    } catch (err) {
      console.error('Ошибка при удалении:', err);
      setError('Не удалось удалить диск.');
    }
  };

  return (
    <Box p={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Реестр дисков</Typography>
        <Button variant="contained" color="primary" onClick={handleAddClick}>
          Добавить диск
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <TextField
          label="Поиск по названию"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setPage(1);
            setSearchQuery(e.target.value);
          }}
          sx={{ width: 300 }}
        />

        <Box display="flex" alignItems="center" gap={3}>
          <Box display="flex" alignItems="center">
            <input
              type="checkbox"
              checked={prioritizeMy}
              onChange={(e) => {
                setPage(1);
                setPrioritizeMy(e.target.checked);
              }}
              id="prioritizeMy"
            />
            <label htmlFor="prioritizeMy" style={{ marginLeft: 8, fontSize: 14 }}>
              Сначала мои диски
            </label>
          </Box>

          <Box display="flex" alignItems="center">
            <input
              type="checkbox"
              checked={unassignedOnly}
              onChange={(e) => {
                setPage(1);
                setUnassignedOnly(e.target.checked);
              }}
              id="unassignedOnly"
            />
            <label htmlFor="unassignedOnly" style={{ marginLeft: 8, fontSize: 14 }}>
              Без привязки к авто
            </label>
          </Box>
          <Box display="flex" alignItems="center">
            <input
              type="checkbox"
              checked={bestseller}
              onChange={(e) => {
                setPage(1);
                setBestseller(e.target.checked);
              }}
              id="bestseller"
            />
            <label htmlFor="bestseller" style={{ marginLeft: 8, fontSize: 14 }}>
              Сначала хиты продаж
            </label>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : wheels.length === 0 ? (
        <Typography>Список дисков пуст.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>№</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Источник</TableCell>

                <TableCell sortDirection={sortBy === 'diameter' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'diameter'}
                    direction={sortBy === 'diameter' ? sortDir : 'asc'}
                    onClick={() => handleSort('diameter')}
                  >
                    Диаметр
                  </TableSortLabel>
                </TableCell>

                <TableCell>Тип</TableCell>

                <TableCell sortDirection={sortBy === 'pcd' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'pcd'}
                    direction={sortBy === 'pcd' ? sortDir : 'asc'}
                    onClick={() => handleSort('pcd')}
                  >
                    PCD
                  </TableSortLabel>
                </TableCell>

                <TableCell>ET</TableCell>
                <TableCell>DIA</TableCell>
                <TableCell>Ширина</TableCell>
                <TableCell>Цена</TableCell>
                <TableCell>Хит продаж</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {wheels.map((wheel, index) => (
                <TableRow
                  key={wheel._id}
                  hover
                  onClick={() => navigate(`/wheels/${wheel._id}`)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: wheel.isMy ? '#8ce78fff' : 'inherit',
                  }}
                >
                  <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                  <TableCell>{wheel.name}</TableCell>
                  <TableCell>{getWheelSourceLabel(wheel.source)}</TableCell>
                  <TableCell>{wheel.diameter}</TableCell>
                  <TableCell>{wheel.type}</TableCell>
                  <TableCell>{wheel.pcd}</TableCell>
                  <TableCell>{wheel.et}</TableCell>
                  <TableCell>{wheel.dia}</TableCell>
                  <TableCell>{wheel.width}</TableCell>
                  <TableCell>
                    {Number(wheel.price) ? `${Number(wheel.price).toLocaleString()} ₽` : '—'}
                  </TableCell>
                  <TableCell>{wheel.bestseller ? 'Да' : 'Нет'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(wheel._id);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default WheelRegistryPage;
